/**
 * "Is it doing anything, and why has it not traded?"
 *
 * Those are the two questions an operator actually asks, and until now both
 * could only be answered with SQL. Signals and job runs are shown together on
 * one screen because the answer is usually in the relationship between them:
 * no signals means the strategies found nothing; signals with no orders means
 * the risk engine refused; no job runs at all means the scheduler is not
 * running.
 */
import { useCallback, useEffect, useState } from 'react';

import { fetchJobRuns, fetchSignals } from '../../api/endpoints';
import type { JobRunRow, SignalRow } from '../../api/types';
import { Notice } from '../../components/Notice';
import { describe as describeError } from '../../lib/errors';
import { formatDateTime, formatMoney, formatQuantity } from '../../lib/format';

function Outcome({ value }: { value: JobRunRow['outcome'] }) {
  const tone =
    value === 'OK' ? 'gain' : value === 'FAILED' ? 'loss' : value === 'LOCKED' ? 'muted' : '';
  return <span className={tone}>{value}</span>;
}

export function OperationsPage() {
  const [signals, setSignals] = useState<SignalRow[]>([]);
  const [runs, setRuns] = useState<JobRunRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [signalRows, runRows] = await Promise.all([fetchSignals(50), fetchJobRuns(50)]);
      setSignals(signalRows);
      setRuns(runRows);
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const schedulerLooksIdle = !loading && runs.length === 0;

  return (
    <section className="page">
      <header className="page__header">
        <h1>Operations</h1>
        <p className="page__lead">What the system has decided and what it has been running.</p>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}

      {schedulerLooksIdle ? (
        <Notice tone="warn">
          No job has run yet. The scheduler is what evaluates strategies and places
          orders — start it with <code>docker compose up -d scheduler</code>.
        </Notice>
      ) : null}

      {!loading && runs.length > 0 && signals.length === 0 ? (
        <Notice tone="info">
          The scheduler is running but no signals have been produced yet. That usually
          means there is not enough candle history, or no strategy is enabled.
        </Notice>
      ) : null}

      <h2>Recent signals</h2>
      {signals.length === 0 ? (
        <p className="muted">Nothing yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Symbol</th>
              <th>Strategy</th>
              <th>Action</th>
              <th className="num">Score</th>
              <th className="num">Price</th>
              <th>Reasoning</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((signal) => (
              <tr key={signal.id} data-testid={`signal-${signal.id}`}>
                <td>{formatDateTime(signal.at)}</td>
                <td>{signal.symbol}</td>
                <td className="muted">{signal.strategy}</td>
                <td className={signal.action === 'ENTER' ? 'gain' : 'loss'}>{signal.action}</td>
                <td className="num">{formatQuantity(signal.score, 1)}</td>
                <td className="num">{formatMoney(signal.reference_price)}</td>
                <td className="muted">
                  {(signal.reasons ?? []).map((reason) => reason.detail).join('; ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Scheduled jobs</h2>
      {runs.length === 0 ? (
        <p className="muted">No runs recorded.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Started</th>
              <th className="num">Took</th>
              <th>Outcome</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id} data-testid={`run-${run.id}`}>
                <td>{run.job}</td>
                <td>{formatDateTime(run.started_at)}</td>
                <td className="num">{run.duration_ms !== null ? `${run.duration_ms}ms` : '—'}</td>
                <td>
                  <Outcome value={run.outcome} />
                </td>
                <td className="muted">
                  {run.error ?? (run.detail ? JSON.stringify(run.detail) : '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
